/*
 * Copyright (c) 2024, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

// Adyen Web fetches `translations/<locale>.json` from its CDN and silently falls back to
// en-US when the locale is not published (fr-CA, en-CA, en-GB, en-IE, nl-BE, de-AT, en-AU
// all 404), which leaves built-in validation errors in English on localized storefronts.
const ADYEN_SUPPORTED_LOCALES = [
    'cs-CZ',
    'da-DK',
    'de-DE',
    'el-GR',
    'en-US',
    'es-ES',
    'fi-FI',
    'fr-FR',
    'hr-HR',
    'hu-HU',
    'it-IT',
    'ja-JP',
    'ko-KR',
    'nl-NL',
    'no-NO',
    'pl-PL',
    'pt-BR',
    'pt-PT',
    'ro-RO',
    'ru-RU',
    'sk-SK',
    'sl-SI',
    'sv-SE',
    'zh-CN',
    'zh-TW'
]

const ADYEN_LANGUAGE_FALLBACKS = {
    cs: 'cs-CZ',
    da: 'da-DK',
    de: 'de-DE',
    el: 'el-GR',
    en: 'en-US',
    es: 'es-ES',
    fi: 'fi-FI',
    fr: 'fr-FR',
    hr: 'hr-HR',
    hu: 'hu-HU',
    it: 'it-IT',
    ja: 'ja-JP',
    ko: 'ko-KR',
    nl: 'nl-NL',
    no: 'no-NO',
    pl: 'pl-PL',
    pt: 'pt-PT',
    ro: 'ro-RO',
    ru: 'ru-RU',
    sk: 'sk-SK',
    sl: 'sl-SI',
    sv: 'sv-SE',
    zh: 'zh-CN'
}

export const ADYEN_DEFAULT_LOCALE = 'en-US'

/**
 * Maps a storefront locale id onto the closest locale Adyen Web ships translations for.
 *
 * @param {string} localeId storefront locale, e.g. `fr-CA`
 * @returns {string} Adyen locale, e.g. `fr-FR`
 */
export const getAdyenLocale = (localeId) => {
    if (!localeId) return ADYEN_DEFAULT_LOCALE

    const normalized = localeId.replace('_', '-')
    const [language, country] = normalized.split('-')
    const candidate = country
        ? `${language.toLowerCase()}-${country.toUpperCase()}`
        : language.toLowerCase()

    if (ADYEN_SUPPORTED_LOCALES.includes(candidate)) return candidate

    return ADYEN_LANGUAGE_FALLBACKS[language.toLowerCase()] || ADYEN_DEFAULT_LOCALE
}
